import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_URL } from '../../utils/api';
import { getNewsImageUrl } from '../../utils/formatters';
import { currentLocale } from '../../i18n';
import { NEWS_CATEGORY_MAP } from './newsConstants';
import NewsComments from './NewsComments';
import './NewsDetail.css';

/**
 * Format an ISO timestamp as a long localised date (e.g. "7 juni 2026").
 *
 * @param {string} iso - ISO datetime string from the API.
 * @returns {string} Localised date, or '' when missing.
 */
function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(currentLocale(), {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Public detail page for a single on-site news post. Renders the cover image,
 * category badge, title, date and the markdown body. (External posts link out
 * from the hub and never reach this page.)
 *
 * @returns {JSX.Element} The news detail page.
 */
export default function NewsDetail() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetch(`${API_URL}/api/news/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then((data) => {
        if (!cancelled) {
          setPost(data);
          setStatus('ready');
        }
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [slug]);

  if (status === 'loading') {
    return <div className="news-detail"><div className="news-loading"><div className="loader" /></div></div>;
  }

  if (status === 'error' || !post) {
    return (
      <div className="news-detail">
        <button type="button" className="news-back" onClick={() => navigate('/news')}>← {t('nav.news')}</button>
        <p className="news-empty">{t('news.notFound')}</p>
      </div>
    );
  }

  const cat = NEWS_CATEGORY_MAP[post.category];
  const cover = getNewsImageUrl(post.cover_image_url);

  return (
    <article className="news-detail">
      <button type="button" className="news-back" onClick={() => navigate('/news')}>← {t('nav.news')}</button>

      <div className="news-detail-meta">
        {cat && <span className="news-detail-badge">{cat.emoji} {t(cat.labelKey)}</span>}
        <span className="news-detail-date">{formatDate(post.published_at || post.created_at)}</span>
      </div>

      <h1 className="news-detail-title">{post.title}</h1>

      {cover && (
        <img className="news-detail-cover" src={cover} alt="" />
      )}

      {post.external_url && (
        <p className="news-detail-external">
          <a href={post.external_url} target="_blank" rel="noopener noreferrer">
            {t('news.openLink')}
          </a>
        </p>
      )}

      <div className="news-detail-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body || ''}</ReactMarkdown>
      </div>

      <NewsComments newsId={post.id} />
    </article>
  );
}
